import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { isArray } from 'lodash-es';
import { IFactDetails, ISections, ITableRel } from './api.models';

export type AbstractControlWarn = AbstractControl & {
    warnings?: Record<string, string>;
};

export function invalidFormatWarningValidator(pattern?: string): ValidatorFn {
    return (control: AbstractControlWarn): ValidationErrors | null => {
        if (!control.value || !pattern) {
            control.warnings = {};
            return null;
        }
        if (isArray(control.value)) {
            control.warnings = {};
            control.value.forEach((value: string) => {
                const formattedValue = formatDateToMatchPattern(value);
                const isInvalidFormat = !RegExp(pattern).test(formattedValue);
                if (isInvalidFormat) {
                    control.warnings = { invalidFormat: getInvalidFormatMessage(formattedValue) };
                    return;
                }
            });
        } else {
            const formattedValue = formatDateToMatchPattern(control.value);
            const isInvalidFormat = !RegExp(pattern).test(formattedValue);
            control.warnings = isInvalidFormat ? { invalidFormat: getInvalidFormatMessage(formattedValue) } : {};
        }

        if (control.warnings && Object.keys(control.warnings).length) {
            return { invalidFormat: 'Validation error' };
        }

        return null;
    };
}

function formatDateToMatchPattern(value: string): string {
    if (isDate(value)) {
        const date = new Date(value);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
        const year = date.getFullYear();
        // Format as dd/mm/yyyy or dd-mm-yyyy based on requirement
        return `${day}/${month}/${year}`;
    }
    return value;
}

function isDate(value: string): boolean {
    const isoDatePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;
    const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
    return isoDatePattern.test(value) || dateOnlyPattern.test(value);
}

function getInvalidFormatMessage(value: string): string {
    return `Value '${value}' is not in expected format`;
}

export function invalidRelationValidator(tableRelations: ITableRel[]): ValidatorFn {
    return (control: AbstractControlWarn): ValidationErrors | null => {
        const table1 = control.get('table1')?.value;
        const column1 = control.get('column1')?.value;
        const table2 = control.get('table2')?.value;
        const column2 = control.get('column2')?.value;
        const dataType = control.get('dataType')?.value;
        if (!table1 || !column1 || !table2 || !column2) {
            control.warnings = {};
            return null;
        }
        const invalidRelations = tableRelations.filter((relation) => {
            return (
                relation.table1 === table1 &&
                relation.column1 === column1 &&
                relation.table2 === table2 &&
                relation.column2 === column2 &&
                relation.dataType == dataType
            );
        });

        if (invalidRelations.length > 0) {
            control.warnings = { invalidRelation: 'This relation already exists' };
            return { invalidRelation: 'Relation already exists' };
        }

        if (table1 === table2) {
            control.warnings = { invalidRelation: 'Self relation is not allowed' };
            return { invalidRelation: 'Self relation not allowed' };
        }

        if (table1 === column1 || table2 === column2) {
            control.warnings = { invalidRelation: 'Table and column names cannot be the same' };
            return { invalidRelation: 'Same table and column name' };
        }

        return null;
    };
}

export function invalidFactTableValidator(factDetails: IFactDetails[]): ValidatorFn {
    return (control: AbstractControlWarn): ValidationErrors | null => {
        const table = control?.value as string;
        if (factDetails.find((fact) => fact.table === table)) {
            control.warnings = { invalidFactTable: 'This fact table already exists' };
            return { invalidFactTable: 'Fact table already exists' };
        }

        return null;
    };
}

export function invalidSqlViewDefinitionValidator(): ValidatorFn {
    return (control: AbstractControlWarn): ValidationErrors | null => {
        const definition = control?.value as string;

        if (!definition) {
            control.warnings = {};
            return null;
        }
        const pattern = /CREATE|UPDATE|INSERT|DELETE|DROP|EXECUTE|EXEC|ALTER|ATTACH|DETACH|--|\/\*|\*\/|;|GO|USE|sp_executesql|DECLARE|SET|CONVERT|@/i;

        if (pattern.test(definition)) {
            control.warnings = { invalidSqlViewDefinition: 'SQL contains reserved words or chars' };
            return { invalidSqlViewDefinition: 'SQL contains reserved words or chars' };
        }

        return null;
    };
}

export function invalidSectionValidator(sections: ISections[], currentSectionId?: number): ValidatorFn {
    return (control: AbstractControlWarn): ValidationErrors | null => {
        const name = control.get('name')?.value;
        const type = control.get('type')?.value;
        const dataType = control.get('dataType')?.value;

        if (!name || !type || !dataType) {
            control.warnings = {};
            return null;
        }
        const invalid = sections.filter((section) => {
            return section.name === name && section.type === type && section.dataType == dataType && section.id !== currentSectionId;
        });

        if (invalid.length > 0) {
            control.warnings = { invalidSection: 'This section already exists' };
            return { invalidSection: 'Section already exists' };
        }

        return null;
    };
}
